import {
  createContext,
  useContext,
  useState,
  useLayoutEffect,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { LucideIcon } from "lucide-react";
import { DocTabProvider } from "./DocTabContext";

export interface PageHeaderState {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  /** CASEBACK #2602: a back control the topbar renders before the title. */
  backTo?: { label: string; to: string };
}

type NoPageActions<T> = Extract<keyof T, "actions" | "secondaryActions"> extends never ? unknown : never;

/**
 * Two-context pattern to avoid infinite render loops.
 *
 * SetHeaderContext: holds the stable setState function. Pages subscribe to this
 * so they can call setHeader without re-rendering when the header value changes.
 *
 * HeaderValueContext: holds the current header state. Only the topbar slot
 * subscribes to this, so only it re-renders when the header changes.
 */
const SetHeaderContext = createContext<Dispatch<SetStateAction<PageHeaderState | null>>>(() => {});
const HeaderValueContext = createContext<PageHeaderState | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeader] = useState<PageHeaderState | null>(null);

  return (
    <SetHeaderContext.Provider value={setHeader}>
      <HeaderValueContext.Provider value={header}>
        {/* #3564: the hub tab on screen, for "About this page". */}
        <DocTabProvider>{children}</DocTabProvider>
      </HeaderValueContext.Provider>
    </SetHeaderContext.Provider>
  );
}

/** Consumed by the topbar slot to render page identity and navigation. */
export function usePageHeader() {
  return useContext(HeaderValueContext);
}

/**
 * Called by each page to declare its header content.
 * Subscribes to SetHeaderContext (stable) so the page does NOT re-render
 * when the header value changes — only the topbar slot re-renders.
 */
export function useSetPageHeader<T extends PageHeaderState>(opts: T & NoPageActions<T>) {
  const setHeader = useContext(SetHeaderContext);

  // Update on every render to keep dynamic title and subtitle values fresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    setHeader(opts);
  });

  // Clear header on unmount only (separate effect to avoid null flash between re-renders)
  useLayoutEffect(() => {
    return () => setHeader(null);
  }, [setHeader]);
}
